import { Grid, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { Download, File, X } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import { Loader } from '~components/reuseable/Loader';
import DateHelper from '~helpers/DateHelper';
import NavigationHelper from '~helpers/NavigationHelper';
import { FileIconHelper } from '~modules/attachedFiles/helpers/FileIconHelper';
import { UploadedFileModel } from '~modules/attachedFiles/models/UploadedFileModel';
import { UploadingStateEnum } from '~modules/attachedFiles/types/UploadingStateEnum';

interface IDocumentPreviewProps {
  file: UploadedFileModel;
  onDelete: (file: UploadedFileModel) => void;
  readonly?: boolean;
}

export const DocumentPreview: React.FC<IDocumentPreviewProps> = (props) => {
  const { file, onDelete, readonly } = props;

  const { palette } = useTheme();
  const { classes } = useStyles();

  const { t } = useTranslation();

  const handleOpenFile = (url: string | null) => {
    if (!url) {
      return;
    }

    NavigationHelper.openInNewTab(`${import.meta.env.VITE_APP_API_URL}${url}`);
  };

  const handleDeleteFile = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    onDelete(file);
  };

  const renderFileIcon = () => {
    const iconColor = file.uploadingState === UploadingStateEnum.ERROR ? palette.error.main : palette.custom.icon.gray;
    const FileIcon = FileIconHelper.getFileIcon(file.fileExt?.toLowerCase());

    if (FileIcon) {
      return <FileIcon color={iconColor} />;
    }

    return <File size={24} color={iconColor} />;
  };

  const renderFileDescr = () => {
    if (file.uploadingState === UploadingStateEnum.LOADING) {
      // Если файл уже был загружен (есть documentUrl), то это удаление
      const isDeleting = file.documentUrl !== null;
      return (
        <Typography variant="body2">
          {isDeleting ? t('input.fileDropzone.deleting') : t('input.fileDropzone.loading')}
        </Typography>
      );
    }

    if (file.uploadingState === UploadingStateEnum.ERROR) {
      return (
        <Typography variant="body2" color="error.main">
          {t('error.uploadError')}
        </Typography>
      );
    }

    return <Typography variant="body2">{file.descr}</Typography>;
  };

  const renderFileActions = () => {
    if (file.uploadingState === UploadingStateEnum.LOADING) {
      return <Loader size={24} />;
    }

    if ((file.uploadingState === UploadingStateEnum.ERROR || file.isCanDelete) && !readonly) {
      return (
        <IconButton size="small" onClick={handleDeleteFile}>
          <X size={24} />
        </IconButton>
      );
    }
  };

  return (
    <Grid container rowSpacing={1} columnSpacing={2} alignItems="center">
      <Grid item xs="auto" order={1}>
        {renderFileIcon()}
      </Grid>

      <Grid item xs order={2}>
        {renderFileDescr()}
      </Grid>

      {file.fixedType?.descr && (
        <Grid item xs={12} lg className={classes.ellipsisContainer} order={{ xs: 4, md: 7, lg: 3 }}>
          <Tooltip title={file.fixedType?.descr}>
            <Typography variant="body2" noWrap className={classes.ellipsisValue}>
              {file.fixedType?.descr}
            </Typography>
          </Tooltip>
        </Grid>
      )}

      <Grid item xs={6} md="auto" order={{ xs: 5, md: 3, lg: 4 }}>
        <Typography variant="body2" color="text.disabled">
          {DateHelper.format(file.dateInsert, 'dd.MM.yyyy HH:mm')}
        </Typography>
      </Grid>

      <Grid item xs={12} md={2} className={classes.ellipsisContainer} order={{ xs: 7, md: 4, lg: 5 }}>
        <Tooltip title={file.empFioInsert}>
          <Typography variant="body2" noWrap className={classes.ellipsisValue}>
            {file.empFioInsert}
          </Typography>
        </Tooltip>
      </Grid>

      <Grid item xs={6} md="auto" order={{ xs: 6, md: 4, lg: 6 }}>
        <Typography variant="body2" color="text.disabled" textAlign="right" width={70}>
          {file.fileSize}
        </Typography>
      </Grid>

      <Grid item xs="auto" order={{ xs: 3, md: 6, lg: 7 }}>
        <Stack direction="row" alignItems="center" justifyContent="flex-end" width={74}>
          {file.uploadingState === UploadingStateEnum.SUCCESS && (
            <IconButton size="medium" onClick={() => handleOpenFile(file.documentUrl)}>
              <Download size={24} color={palette.primary.main} />
            </IconButton>
          )}

          {renderFileActions()}
        </Stack>
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles()(() => ({
  ellipsisContainer: {
    minWidth: 0,
  },
  ellipsisValue: {
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));
